import React, { useState, useRef, useEffect } from 'react';
import { Button, Card, Form, Input, message, Typography, Alert } from 'antd';
import { KeyOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../http/API';

const { Title, Text } = Typography;

interface ResetPasswordResponse {
    status: boolean;
    message: string;
}

const RESEND_COOLDOWN = 60;

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    // Email и код могут прийти либо из state (при переходе с формы
    // forgot-password), либо из query-параметров (по ссылке из письма).
    const params = new URLSearchParams(location.search);
    const emailFromState = (location.state as { email?: string } | null)?.email;
    const emailFromQuery = params.get('email') || '';
    const email = emailFromState || emailFromQuery;
    const codeFromQuery = params.get('code') || '';

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Если пользователь открыл /reset-password напрямую без email — вернуть на форму.
    useEffect(() => {
        if (!email) {
            navigate('/forgot-password', { replace: true });
        }
    }, [email, navigate]);

    // Таймер обратного отсчёта для кнопки «Отправить повторно».
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setInterval(() => {
            setCooldown((c) => (c > 0 ? c - 1 : 0));
        }, 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const onFinish = async (values: { code: string; newPassword: string; confirm: string }) => {
        setLoading(true);
        try {
            const response = await api.ResetPassword(values.code, values.newPassword) as ResetPasswordResponse;

            if (!isMounted.current) return;

            if (response.status === false) {
                message.error(response.message || 'Неверный или просроченный код');
                setLoading(false);
                return;
            }

            message.success('Пароль успешно изменён. Теперь войдите.');
            navigate('/login');
        } catch (error: any) {
            if (!isMounted.current) return;

            if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else if (error.response?.data && typeof error.response.data === 'string') {
                message.error(error.response.data);
            } else {
                message.error('Ошибка при сбросе пароля');
            }
            setLoading(false);
        }
    };

    const resendCode = async () => {
        if (cooldown > 0) return;
        setResending(true);
        try {
            await api.ForgotPassword(email);
            message.success('Код отправлен повторно');
            setCooldown(RESEND_COOLDOWN);
        } catch (error: any) {
            if (error?.response?.status === 429) {
                message.warning('Слишком часто. Подождите минуту.');
                setCooldown(RESEND_COOLDOWN);
            } else {
                message.error('Не удалось отправить код');
            }
        } finally {
            setResending(false);
        }
    };

    const validateConfirmPassword = ({ getFieldValue }: any) => ({
        validator(_: any, value: string) {
            if (!value || getFieldValue('newPassword') === value) {
                return Promise.resolve();
            }
            return Promise.reject(new Error('Пароли не совпадают'));
        },
    });

    if (!email) {
        return null;
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
            <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <Title level={3} style={{ textAlign: 'center' }}>Сброс пароля</Title>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={
                        <>
                            Код отправлен на <strong>{email}</strong>
                        </>
                    }
                    action={
                        <Button
                            size="small"
                            type="link"
                            onClick={() => navigate('/forgot-password')}
                        >
                            Изменить
                        </Button>
                    }
                />
                <Form
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={codeFromQuery ? { code: codeFromQuery } : undefined}
                >
                    <Form.Item
                        label="Код из письма"
                        name="code"
                        rules={[
                            { required: true, message: 'Введите код' },
                            { len: 6, message: 'Код должен состоять из 6 цифр' },
                        ]}
                    >
                        <Input prefix={<KeyOutlined />} placeholder="6-значный код" maxLength={6} />
                    </Form.Item>
                    <Form.Item
                        label="Новый пароль"
                        name="newPassword"
                        rules={[{ required: true, message: 'Введите новый пароль' }]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Новый пароль" />
                    </Form.Item>
                    <Form.Item
                        label="Подтверждение пароля"
                        name="confirm"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Подтвердите пароль' },
                            validateConfirmPassword,
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Повторите пароль" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loading}>
                            Сбросить пароль
                        </Button>
                    </Form.Item>
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="link"
                            onClick={resendCode}
                            disabled={cooldown > 0}
                            loading={resending}
                        >
                            {cooldown > 0
                                ? `Отправить повторно через ${cooldown} с`
                                : 'Отправить код повторно'}
                        </Button>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <Button type="link" onClick={() => navigate('/login')}>
                            Вернуться ко входу
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default ResetPasswordPage;
